/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AUTO_STYLE, AnimateTimings, AnimationAnimateMetadata, AnimationGroupMetadata, AnimationKeyframesSequenceMetadata, AnimationMetadata, AnimationMetadataType, AnimationSequenceMetadata, AnimationStateMetadata, AnimationStyleMetadata, AnimationTransitionMetadata, sequence, style, ɵStyleData} from '@angular/animations';

import {copyStyles, normalizeStyles, parseTimeExpression} from '../util';

import {AnimationDslVisitor, visitAnimationNode} from './animation_dsl_visitor';
import {AnimationTimelineInstruction, createTimelineInstruction} from './animation_timeline_instruction';
/**
 * @param {?} ast
 * @param {?=} startingStyles
 * @param {?=} finalStyles
 * @return {?}
 */
export function buildAnimationKeyframes(
    ast: AnimationMetadata | AnimationMetadata[], startingStyles: ɵStyleData = {},
    finalStyles: ɵStyleData = {}): AnimationTimelineInstruction[] {
  const /** @type {?} */ normalizedAst =
      Array.isArray(ast) ? sequence( /** @type {?} */((<AnimationMetadata[]>ast))) : /** @type {?} */(( <AnimationMetadata>ast));
  return new AnimationTimelineVisitor().buildKeyframes(normalizedAst, startingStyles, finalStyles);
}

export declare type StyleAtTime = {
  time: number; value: string | number;
};
export class AnimationTimelineContext {
  currentTimeline: TimelineBuilder;
  currentAnimateTimings: AnimateTimings|null;
  previousNode: AnimationMetadata = /** @type {?} */(( <AnimationMetadata>{}));
  subContextCount = 0;
/**
 * @param {?} errors
 * @param {?} timelines
 * @param {?=} initialTimeline
 */
constructor(
public errors: any[],
public timelines: TimelineBuilder[],
      initialTimeline?: TimelineBuilder) {
    this.currentTimeline = initialTimeline || new TimelineBuilder(0);
    timelines.push(this.currentTimeline);
  }
/**
 * @return {?}
 */
createSubContext(): AnimationTimelineContext {
    const /** @type {?} */ context =
        new AnimationTimelineContext(this.errors, this.timelines, this.currentTimeline.fork());
    context.previousNode = this.previousNode;
    context.currentAnimateTimings = this.currentAnimateTimings;
    this.subContextCount++;
    return context;
  }
/**
 * @param {?=} newTime
 * @return {?}
 */
transformIntoNewTimeline(newTime = 0) {
    this.currentTimeline = this.currentTimeline.fork(newTime);
    this.timelines.push(this.currentTimeline);
    return this.currentTimeline;
  }
/**
 * @param {?} time
 * @return {?}
 */
incrementTime(time: number) {
    this.currentTimeline.forwardTime(this.currentTimeline.duration + time);
  }
}

function AnimationTimelineContext_tsickle_Closure_declarations() {
/** @type {?} */
AnimationTimelineContext.prototype.currentTimeline;
/** @type {?} */
AnimationTimelineContext.prototype.currentAnimateTimings;
/** @type {?} */
AnimationTimelineContext.prototype.previousNode;
/** @type {?} */
AnimationTimelineContext.prototype.subContextCount;
/** @type {?} */
AnimationTimelineContext.prototype.errors;
/** @type {?} */
AnimationTimelineContext.prototype.timelines;
}

export class AnimationTimelineVisitor implements AnimationDslVisitor {
/**
 * @param {?} ast
 * @param {?} startingStyles
 * @param {?} finalStyles
 * @return {?}
 */
buildKeyframes(ast: AnimationMetadata, startingStyles: ɵStyleData, finalStyles: ɵStyleData):
      AnimationTimelineInstruction[] {
    const /** @type {?} */ context = new AnimationTimelineContext([], []);
    context.currentTimeline.setStyles(startingStyles);

    visitAnimationNode(this, ast, context);

    // this checks to see if an actual animation happened
    const /** @type {?} */ timelines = context.timelines.filter(timeline => timeline.hasStyling());
    if (timelines.length && Object.keys(finalStyles).length) {
      const /** @type {?} */ tl = timelines[timelines.length - 1];
      if (!tl.allowOnlyTimelineStyles()) {
        tl.setStyles(finalStyles);
      }
    }

    return timelines.length ? timelines.map(timeline => timeline.buildKeyframes()) :
                              [createTimelineInstruction([], 0, 0, '')];
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitState(ast: AnimationStateMetadata, context: any): any {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitTransition(ast: AnimationTransitionMetadata, context: any): any {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitSequence(ast: AnimationSequenceMetadata, context: AnimationTimelineContext) {
    const /** @type {?} */ subContextCount = context.subContextCount;
    if (context.previousNode.type == AnimationMetadataType.Style) {
      context.currentTimeline.forwardFrame();
      context.currentTimeline.snapshotCurrentStyles();
    }

    ast.steps.forEach(s => visitAnimationNode(this, s, context));

    // this means that some animation function within the sequence
    // ended up creating a sub timeline (which means the current
    // timeline cannot overlap with the contents of the sequence)
    if (context.subContextCount > subContextCount) {
      context.transformIntoNewTimeline();
    }

    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitGroup(ast: AnimationGroupMetadata, context: AnimationTimelineContext) {
    const /** @type {?} */ innerTimelines: TimelineBuilder[] = [];
    let /** @type {?} */ furthestTime = context.currentTimeline.currentTime;
    ast.steps.forEach(s => {
      const /** @type {?} */ innerContext = context.createSubContext();
      visitAnimationNode(this, s, innerContext);
      furthestTime = Math.max(furthestTime, innerContext.currentTimeline.currentTime);
      innerTimelines.push(innerContext.currentTimeline);
    });

    // this operation is run after the AST loop because otherwise
    // if the parent timeline's collected styles were updated then
    // it would pass in invalid data into the new-to-be forked items
    innerTimelines.forEach(
        timeline => context.currentTimeline.mergeTimelineCollectedStyles(timeline));
    context.transformIntoNewTimeline(furthestTime);
    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitAnimate(ast: AnimationAnimateMetadata, context: AnimationTimelineContext) {
    const /** @type {?} */ timings = ast.timings.hasOwnProperty('duration') ? /** @type {?} */((
        <AnimateTimings>ast.timings)) :
        parseTimeExpression( /** @type {?} */((<string|number>ast.timings)), context.errors);
    context.currentAnimateTimings = timings;

    if (timings.delay) {
      context.incrementTime(timings.delay);
      context.currentTimeline.snapshotCurrentStyles();
    }

    const /** @type {?} */ astType = ast.styles ? ast.styles.type : -1;
    if (astType == AnimationMetadataType.KeyframeSequence) {
      this.visitKeyframeSequence( /** @type {?} */((<AnimationKeyframesSequenceMetadata>ast.styles)), context);
    } else {
      let /** @type {?} */ styleAst = /** @type {?} */(( ast.styles as AnimationStyleMetadata));
      if (!styleAst) {
        const /** @type {?} */ newStyleData: {[prop: string]: string | number} = {};
        if (timings.easing) {
          newStyleData['easing'] = timings.easing;
        }
        styleAst = style(newStyleData);
        ( /** @type {?} */((styleAst as any)))['treatAsEmptyStep'] = true;
      }
      context.incrementTime(timings.duration);
      if (styleAst) {
        this.visitStyle(styleAst, context);
      }
    }

    context.currentAnimateTimings = null;
    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitStyle(ast: AnimationStyleMetadata, context: AnimationTimelineContext) {
    // this is a special case when a style() call is issued directly after
    // a call to animate(). If the clock is not forwarded by one frame then
    // the style() calls will be merged into the previous animate() call
    // which is incorrect.
    if (!context.currentAnimateTimings &&
        context.previousNode.type == AnimationMetadataType.Animate) {
      context.currentTimeline.forwardFrame();
    }

    const /** @type {?} */ normalizedStyles = normalizeStyles(ast.styles);
    const /** @type {?} */ easing = context.currentAnimateTimings && context.currentAnimateTimings.easing;
    this._applyStyles(
        normalizedStyles, easing, ( /** @type {?} */((ast as any)))['treatAsEmptyStep'] ? true : false, context);
    context.previousNode = ast;
  }
/**
 * @param {?} styles
 * @param {?} easing
 * @param {?} treatAsEmptyStep
 * @param {?} context
 * @return {?}
 */
private _applyStyles(
      styles: ɵStyleData, easing: string|null, treatAsEmptyStep: boolean,
      context: AnimationTimelineContext) {
    if (styles.hasOwnProperty('easing')) {
      easing = easing || /** @type {?} */(( styles['easing'] as string));
      delete styles['easing'];
    }
    context.currentTimeline.setStyles(styles, easing, treatAsEmptyStep);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitKeyframeSequence(
      ast: AnimationKeyframesSequenceMetadata, context: AnimationTimelineContext) {
    const /** @type {?} */ MAX_KEYFRAME_OFFSET = 1;
    const /** @type {?} */ limit = ast.steps.length - 1;
    const /** @type {?} */ firstKeyframe = ast.steps[0];

    let /** @type {?} */ offsetGap = 0;
    const /** @type {?} */ containsOffsets = getOffset(firstKeyframe) != null;
    if (!containsOffsets) {
      offsetGap = MAX_KEYFRAME_OFFSET / limit;
    }

    const /** @type {?} */ startTime = context.currentTimeline.duration;
    const /** @type {?} */ duration = /** @type {?} */(( context.currentAnimateTimings)).duration;
    const /** @type {?} */ innerContext = context.createSubContext();
    const /** @type {?} */ innerTimeline = innerContext.currentTimeline;
    innerTimeline.easing = /** @type {?} */(( context.currentAnimateTimings)).easing;

    ast.steps.forEach((step: AnimationStyleMetadata, i: number) => {
      const /** @type {?} */ normalizedStyles = normalizeStyles(step.styles);
      const /** @type {?} */ offset = containsOffsets ?
          (step.offset != null ? step.offset : parseFloat( /** @type {?} */((normalizedStyles['offset'] as string)))) :
          (i == limit ? MAX_KEYFRAME_OFFSET : i * offsetGap);
      innerTimeline.forwardTime(offset * duration);
      this._applyStyles(normalizedStyles, null, false, innerContext);
    });

    // this will ensure that the parent timeline gets all the styles from
    // the child even if the new timeline below is not used
    context.currentTimeline.mergeTimelineCollectedStyles(innerTimeline);

    // we do this because the window between this timeline and the sub timeline
    // should ensure that the styles within are exactly the same as they were before
    context.transformIntoNewTimeline(startTime + duration);
    context.previousNode = ast;
  }
}
export class TimelineBuilder {
public duration: number = 0;
public easing: string|null = '';
private _previousKeyframe: ɵStyleData = {};
private _currentKeyframe: ɵStyleData;
private _keyframes = new Map<number, ɵStyleData>();
private _styleSummary: {[prop: string]: StyleAtTime} = {};
private _localTimelineStyles: ɵStyleData;
private _backFill: ɵStyleData = {};
private _currentEmptyStepKeyframe: ɵStyleData|null = null;
private _globalTimelineStyles: ɵStyleData;
/**
 * @param {?} startTime
 * @param {?=} globalTimelineStyles
 */
constructor(public startTime: number, globalTimelineStyles?: ɵStyleData) {
    this._localTimelineStyles = Object.create(this._backFill, {});
    this._globalTimelineStyles =
        globalTimelineStyles ? globalTimelineStyles : this._localTimelineStyles;
    this._loadKeyframe();
  }
/**
 * @return {?}
 */
hasStyling(): boolean { return this._keyframes.size > 1; }
/**
 * @return {?}
 */
get currentTime() { return this.startTime + this.duration; }
/**
 * @param {?=} currentTime
 * @return {?}
 */
fork(currentTime = 0): TimelineBuilder {
    return new TimelineBuilder(currentTime || this.currentTime, this._globalTimelineStyles);
  }
/**
 * @return {?}
 */
private _loadKeyframe() {
    if (this._currentKeyframe) {
      this._previousKeyframe = this._currentKeyframe;
    }
    this._currentKeyframe = /** @type {?} */(( this._keyframes.get(this.duration)));
    if (!this._currentKeyframe) {
      this._currentKeyframe = Object.create(this._backFill, {});
      this._keyframes.set(this.duration, this._currentKeyframe);
    }
  }
/**
 * @return {?}
 */
forwardFrame() {
    this.duration++;
    this._loadKeyframe();
  }
/**
 * @param {?} time
 * @return {?}
 */
forwardTime(time: number) {
    this.duration = time;
    this._loadKeyframe();
  }
/**
 * @param {?} prop
 * @param {?} value
 * @return {?}
 */
private _updateStyle(prop: string, value: string|number) {
    this._localTimelineStyles[prop] = value; /** @type {?} */((
    this._globalTimelineStyles))[prop] = value;
    this._styleSummary[prop] = {time: this.currentTime, value};
  }
/**
 * @return {?}
 */
allowOnlyTimelineStyles() { return this._currentEmptyStepKeyframe !== this._currentKeyframe; }
/**
 * @param {?} styles
 * @param {?=} easing
 * @param {?=} treatAsEmptyStep
 * @return {?}
 */
setStyles(styles: ɵStyleData, easing: string|null = null, treatAsEmptyStep: boolean = false) {
    if (easing) { /** @type {?} */((
      this._previousKeyframe))['easing'] = easing;
    }

    if (treatAsEmptyStep) {
      // special case for animate(duration):
      // all missing styles are filled with a `*` value then
      // if any destination styles are filled in later on the same
      // keyframe then they will override the overridden styles
      // We use `_globalTimelineStyles` here because there may be
      // styles in previous keyframes that are not present in this timeline
      Object.keys(this._globalTimelineStyles).forEach(prop => {
        this._backFill[prop] = this._globalTimelineStyles[prop] || AUTO_STYLE;
        this._currentKeyframe[prop] = AUTO_STYLE;
      });
      this._currentEmptyStepKeyframe = this._currentKeyframe;
    } else {
      Object.keys(styles).forEach(prop => {
        if (prop !== 'offset') {
          const /** @type {?} */ val = styles[prop];
          this._currentKeyframe[prop] = val;
          if (!this._localTimelineStyles[prop]) {
            this._backFill[prop] = this._globalTimelineStyles[prop] || AUTO_STYLE;
          }
          this._updateStyle(prop, val);
        }
      });

      Object.keys(this._localTimelineStyles).forEach(prop => {
        if (!this._currentKeyframe.hasOwnProperty(prop)) {
          this._currentKeyframe[prop] = this._localTimelineStyles[prop];
        }
      });
    }
  }
/**
 * @return {?}
 */
snapshotCurrentStyles() { copyStyles(this._localTimelineStyles, false, this._currentKeyframe); }
/**
 * @return {?}
 */
getFinalKeyframe(): ɵStyleData { return /** @type {?} */(( this._keyframes.get(this.duration))); }
/**
 * @return {?}
 */
get properties() {
    const /** @type {?} */ properties: string[] = [];
    for (let /** @type {?} */ prop in this._currentKeyframe) {
      properties.push(prop);
    }
    return properties;
  }
/**
 * @param {?} timeline
 * @return {?}
 */
mergeTimelineCollectedStyles(timeline: TimelineBuilder) {
    Object.keys(timeline._styleSummary).forEach(prop => {
      const /** @type {?} */ details0 = this._styleSummary[prop];
      const /** @type {?} */ details1 = timeline._styleSummary[prop];
      if (!details0 || details1.time > details0.time) {
        this._updateStyle(prop, details1.value);
      }
    });
  }
/**
 * @return {?}
 */
buildKeyframes(): AnimationTimelineInstruction {
    const /** @type {?} */ finalKeyframes: ɵStyleData[] = [];
    // special case for when there are only start/destination
    // styles but no actual animation animate steps...
    if (this.duration == 0) {
      const /** @type {?} */ targetKeyframe = this.getFinalKeyframe();

      const /** @type {?} */ firstKeyframe = copyStyles(targetKeyframe, true);
      firstKeyframe['offset'] = 0;
      finalKeyframes.push(firstKeyframe);

      const /** @type {?} */ lastKeyframe = copyStyles(targetKeyframe, true);
      lastKeyframe['offset'] = 1;
      finalKeyframes.push(lastKeyframe);
    } else {
      this._keyframes.forEach((keyframe, time) => {
        const /** @type {?} */ finalKeyframe = copyStyles(keyframe, true);
        finalKeyframe['offset'] = time / this.duration;
        finalKeyframes.push(finalKeyframe);
      });
    }

    return createTimelineInstruction(finalKeyframes, this.duration, this.startTime, this.easing);
  }
}

function TimelineBuilder_tsickle_Closure_declarations() {
/** @type {?} */
TimelineBuilder.prototype.duration;
/** @type {?} */
TimelineBuilder.prototype.easing;
/** @type {?} */
TimelineBuilder.prototype._previousKeyframe;
/** @type {?} */
TimelineBuilder.prototype._currentKeyframe;
/** @type {?} */
TimelineBuilder.prototype._keyframes;
/** @type {?} */
TimelineBuilder.prototype._styleSummary;
/** @type {?} */
TimelineBuilder.prototype._localTimelineStyles;
/** @type {?} */
TimelineBuilder.prototype._backFill;
/** @type {?} */
TimelineBuilder.prototype._currentEmptyStepKeyframe;
/** @type {?} */
TimelineBuilder.prototype._globalTimelineStyles;
/** @type {?} */
TimelineBuilder.prototype.startTime;
}

/**
 * @param {?} ast
 * @return {?}
 */
function getOffset(ast: AnimationStyleMetadata): number {
  let /** @type {?} */ offset = ast.offset;
  if (offset == null) {
    const /** @type {?} */ styles = ast.styles;
    if (Array.isArray(styles)) {
      for (let /** @type {?} */ i = 0; i < styles.length; i++) {
        const /** @type {?} */ o = /** @type {?} */(( styles[i]['offset'] as number));
        if (o != null) {
          offset = o;
          break;
        }
      }
    } else {
      offset = /** @type {?} */(( styles['offset'] as number));
    }
  }
  return /** @type {?} */(( offset));
}
